import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export interface BatchJob {
  id: string;
  user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_count: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
  csv_file_url: string | null;
  result_zip_url: string | null;
  design_settings: Record<string, unknown>;
  error_log: Array<{ row: number; error: string }>;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface BatchRowData {
  name: string;
  type: string;
  [key: string]: string;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

// Plan-based batch limits (0 means no access)
export const batchLimits = {
  free: 0, // Free users don't have batch access
  starter: 10,
  professional: 100,
  business: 500,
  enterprise: -1, // unlimited
};

export function useBatchJobs() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: batchJobs = [], isLoading } = useQuery({
    queryKey: ['batch-jobs', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('batch_jobs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data as unknown as BatchJob[];
    },
    enabled: !!user,
  });

  const createBatchJob = useMutation({
    mutationFn: async (params: {
      totalCount: number;
      designSettings: Record<string, unknown>;
    }) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('batch_jobs')
        .insert({
          user_id: user.id,
          total_count: params.totalCount,
          design_settings: params.designSettings as Json,
          status: 'pending',
        })
        .select()
        .single();
        
      if (error) throw error;
      return data as unknown as BatchJob;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-jobs'] });
    },
    onError: (error) => {
      toast.error('Failed to create batch job: ' + error.message);
    },
  });

  const updateBatchJob = useMutation({
    mutationFn: async (params: {
      id: string;
      updates: Partial<BatchJob>;
    }) => {
      const { error } = await supabase
        .from('batch_jobs')
        .update({
          ...params.updates,
          error_log: params.updates.error_log as unknown as Json,
          design_settings: params.updates.design_settings as Json,
        })
        .eq('id', params.id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-jobs'] });
    },
  });

  const deleteBatchJob = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('batch_jobs')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-jobs'] });
      toast.success('Batch job deleted');
    },
  });

  return {
    batchJobs,
    isLoading,
    createBatchJob,
    updateBatchJob,
    deleteBatchJob,
  };
}

// CSV validation utilities
export function validateBatchRow(row: BatchRowData, rowIndex: number): ValidationError[] {
  const errors: ValidationError[] = [];
  
  // Required fields
  if (!row.name?.trim()) {
    errors.push({ row: rowIndex, field: 'name', message: 'Name is required' });
  }
  
  if (!row.type?.trim()) {
    errors.push({ row: rowIndex, field: 'type', message: 'QR type is required' });
  }
  
  // Type-specific validation
  const type = row.type?.toLowerCase();
  
  if (type === 'url') {
    if (!row.url?.trim()) {
      errors.push({ row: rowIndex, field: 'url', message: 'URL is required' });
    } else if (!/^https?:\/\/.+/.test(row.url)) {
      errors.push({ row: rowIndex, field: 'url', message: 'Invalid URL format' });
    }
  }
  
  if (type === 'email') {
    if (!row.email?.trim()) {
      errors.push({ row: rowIndex, field: 'email', message: 'Email is required' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      errors.push({ row: rowIndex, field: 'email', message: 'Invalid email format' });
    }
  }
  
  if (type === 'phone' || type === 'sms' || type === 'whatsapp') {
    if (!row.phone?.trim()) {
      errors.push({ row: rowIndex, field: 'phone', message: 'Phone number is required' });
    }
  }
  
  if (type === 'wifi') {
    if (!row.ssid?.trim()) {
      errors.push({ row: rowIndex, field: 'ssid', message: 'SSID is required' });
    }
  }
  
  if (type === 'vcard') {
    if (!row.first_name?.trim() && !row.last_name?.trim()) {
      errors.push({ row: rowIndex, field: 'first_name', message: 'Name is required for vCard' });
    }
  }
  
  return errors;
}

// Generate CSV templates for each QR type
export function generateCSVTemplate(type: string): string {
  const templates: Record<string, { headers: string[]; example: string[] }> = {
    url: {
      headers: ['name', 'type', 'url'],
      example: ['My Website', 'url', 'https://example.com'],
    },
    text: {
      headers: ['name', 'type', 'text'],
      example: ['My Text', 'text', 'Hello World!'],
    },
    email: {
      headers: ['name', 'type', 'email', 'subject', 'body'],
      example: ['Contact Email', 'email', 'hello@example.com', 'Hello', 'Message here'],
    },
    phone: {
      headers: ['name', 'type', 'phone'],
      example: ['Call Us', 'phone', '+1234567890'],
    },
    sms: {
      headers: ['name', 'type', 'phone', 'message'],
      example: ['SMS Campaign', 'sms', '+1234567890', 'Hi there!'],
    },
    whatsapp: {
      headers: ['name', 'type', 'phone', 'message'],
      example: ['WhatsApp Link', 'whatsapp', '+1234567890', 'Hello!'],
    },
    wifi: {
      headers: ['name', 'type', 'ssid', 'password', 'encryption', 'hidden'],
      example: ['Office WiFi', 'wifi', 'MyNetwork', 'MyPassword123', 'WPA', 'false'],
    },
    vcard: {
      headers: ['name', 'type', 'first_name', 'last_name', 'phone', 'email', 'company', 'title', 'website'],
      example: ['John Contact', 'vcard', 'John', 'Doe', '+1234567890', 'john@example.com', 'Acme Inc', 'CEO', 'https://example.com'],
    },
    location: {
      headers: ['name', 'type', 'latitude', 'longitude', 'address'],
      example: ['Office Location', 'location', '40.7128', '-74.0060', 'New York, NY'],
    },
    multi: {
      headers: ['name', 'type', 'url', 'phone', 'email', 'text', 'ssid', 'password', 'encryption', 'hidden', 'first_name', 'last_name', 'company', 'title', 'website', 'latitude', 'longitude', 'address', 'message'],
      example: ['Example QR', 'url', 'https://example.com', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    },
  };
  
  const template = templates[type] || templates.multi;
  return [template.headers.join(','), template.example.join(',')].join('\n');
}

// Get all available QR types for batch
export const batchQRTypes = [
  { value: 'url', label: 'URL/Website' },
  { value: 'text', label: 'Plain Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'sms', label: 'SMS Message' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'wifi', label: 'WiFi Network' },
  { value: 'vcard', label: 'Contact Card (vCard)' },
  { value: 'location', label: 'Location' },
];
